import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { cn } from '../lib/utils';

/**
 * UserAvatar - Reusable component for displaying user identity
 * Shows avatar image with initials fallback and optional name/role display
 */
const UserAvatar = ({ 
  user, 
  size = 'md', 
  showName = false,
  showRole = false,
  className = '',
  nameClassName = '',
  onClick = null
}) => {
  // Handle different data formats
  const displayName = user?.display_name || user?.displayName || 
    (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '') ||
    user?.name || user?.email || 'Unknown';
  
  const avatarUrl = user?.avatar_url || user?.avatarUrl || user?.profile_picture || user?.profilePicture;
  const email = user?.email;
  const role = user?.role;
  
  // Generate initials from display name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  // Size variants
  const sizeClasses = {
    xs: 'h-6 w-6 text-xs',
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
    xl: 'h-16 w-16 text-lg'
  };
  
  const avatarSize = sizeClasses[size] || sizeClasses.md;
  
  // Color palette for fallback backgrounds based on name - Blue as primary
  const colorPalette = [
    'bg-cyan-600', 'bg-blue-600', 'bg-sky-600', 'bg-teal-600',
    'bg-indigo-600', 'bg-cyan-500', 'bg-blue-500', 'bg-sky-500'
  ];
  
  const getBackgroundColor = (name) => {
    const index = name ? name.charCodeAt(0) % colorPalette.length : 0;
    return colorPalette[index];
  };
  
  const Wrapper = onClick ? 'button' : 'div';
  
  return (
    <Wrapper 
      className={cn(
        'flex items-center gap-2',
        onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
        className
      )}
      onClick={onClick}
    >
      <Avatar className={avatarSize}>
        {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
        <AvatarFallback className={cn(getBackgroundColor(displayName), 'text-white font-medium')}>
          {getInitials(displayName)}
        </AvatarFallback>
      </Avatar>
      
      {(showName || showRole) && (
        <div className="flex flex-col min-w-0">
          {showName && (
            <span className={cn('font-medium truncate', nameClassName)}>
              {displayName}
            </span>
          )}
          {showRole && role && (
            <span className="text-xs text-muted-foreground truncate capitalize">
              {role.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      )}
    </Wrapper>
  );
};

/**
 * UserAvatarGroup - Display multiple avatars in a stacked group
 */
export const UserAvatarGroup = ({ 
  users = [], 
  max = 5, 
  size = 'sm',
  showCount = true,
  className = '' 
}) => {
  const displayUsers = users.slice(0, max);
  const remainingCount = users.length - max;
  
  const sizeClasses = {
    xs: 'h-6 w-6 text-xs -ml-2',
    sm: 'h-8 w-8 text-sm -ml-3',
    md: 'h-10 w-10 text-sm -ml-4',
    lg: 'h-12 w-12 text-base -ml-4'
  };
  
  const avatarSize = sizeClasses[size] || sizeClasses.sm;
  
  return (
    <div className={cn('flex items-center', className)}>
      {displayUsers.map((user, index) => (
        <div 
          key={user?.user_id || user?.id || index} 
          className={cn(
            avatarSize,
            'rounded-full ring-2 ring-background',
            index === 0 && 'ml-0'
          )}
          style={{ zIndex: displayUsers.length - index }}
        >
          <UserAvatar user={user} size={size} />
        </div>
      ))}
      
      {showCount && remainingCount > 0 && (
        <div 
          className={cn(
            avatarSize,
            'rounded-full ring-2 ring-background bg-muted flex items-center justify-center font-medium text-muted-foreground'
          )}
          style={{ zIndex: 0 }}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

/**
 * UserBadge - Compact user display with avatar and name inline
 */
export const UserBadge = ({ 
  user, 
  size = 'sm',
  variant = 'default',
  className = '' 
}) => {
  const displayName = user?.display_name || user?.displayName || 
    (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '') ||
    user?.name || user?.email || 'Unknown';
  
  const variantClasses = {
    default: 'bg-muted text-foreground',
    primary: 'bg-primary/10 text-primary',
    success: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    error: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  };
  
  return (
    <div className={cn(
      'inline-flex items-center gap-1.5 px-2 py-1 rounded-full',
      variantClasses[variant],
      className
    )}>
      <UserAvatar user={user} size={size === 'sm' ? 'xs' : 'sm'} />
      <span className="text-sm font-medium truncate max-w-[150px]">{displayName}</span>
    </div>
  );
};

export default UserAvatar;
